import React from 'react';

import { Provider } from 'react-redux';
import { Toaster } from 'react-hot-toast';
import { ApolloProvider, ApolloClient, HttpLink, InMemoryCache } from '@apollo/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { setContext } from '@apollo/client/link/context';
import fetch from 'isomorphic-fetch';
import posthog from 'posthog-js';
import createStore from '@src/store';
import { fetchUser } from '@src/store/user';
import { isPosthogEnabled, posthogHost, posthogKey } from '@src/utils/env';

import './src/assets/styles/index.scss';
import './src/assets/styles/global.css';
import { Layout } from 'components';

const datoCMS = new HttpLink({
  uri: process.env.GATSBY_DATO_CMS_URL,
  headers: {
    'X-Environment': process.env.GATSBY_DATO_CMS_ENVIRONMENT || undefined,
  },
  fetch,
});

const authLink = setContext((_, { headers }) => {
  return {
    headers: {
      ...headers,
      authorization: `Bearer ${process.env.GATSBY_DATO_API_TOKEN}`,
      'X-Environment': process.env.GATSBY_DATO_CMS_ENVIRONMENT || undefined,
    },
  };
});

const client = new ApolloClient({
  cache: new InMemoryCache(),
  link: authLink.concat(datoCMS),
});

const queryClient = new QueryClient();

const identifyUser = (user, identifiedUserId) => {
  if (!isPosthogEnabled || !user?.userId || user.userId === identifiedUserId) {
    return identifiedUserId;
  }

  if (identifiedUserId) {
    posthog.reset();
  }

  const personProperties = {
    role: user.role,
  };

  if (user.email) personProperties.email = user.email;
  if (user.fullname || user.username) personProperties.name = user.fullname || user.username;

  posthog.identify(user.userId, personProperties);
  return user.userId;
};

export const onClientEntry = () => {
  if (!isPosthogEnabled) {
    if (process.env.NODE_ENV === 'development') {
      const missingVar = !posthogKey ? 'GATSBY_POSTHOG_KEY' : 'GATSBY_POSTHOG_HOST';
      throw new Error(
        `${missingVar} variable required by PostHog is missing or un-configured, this causes events to be silently missed. This error stops appearing once ${missingVar} is configured`,
      );
    }

    return;
  }

  posthog.init(posthogKey, {
    api_host: posthogHost,
    defaults: '2026-05-30',
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      capture_console_errors: false,
    },
    debug: true,
  });
};

export const wrapRootElement = ({ element }) => {
  const store = createStore();
  let identifiedUserId;

  store.subscribe(() => {
    identifiedUserId = identifyUser(store.getState().user.data, identifiedUserId);
  });
  store.dispatch(fetchUser());

  return (
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        <ApolloProvider client={client}>
          {/*
            Instant search is currently tied to the question index.
            We had issues using multiple <Index/> widgets for the same index name and syncing the search state between list/details view.
            Maybe revisit this in the future if we need more "indexes".
          */}
          {element}
        </ApolloProvider>
      </Provider>
      <Toaster position="bottom-center" />
    </QueryClientProvider>
  );
};

export const wrapPageElement = ({ element, props }) => (
  <Layout location={props.location}>{element}</Layout>
);

export const onInitialClientRender = () => {
  var isChromeBased = /Chrome/.test(navigator.userAgent);

  const sessionStorageKeys = Object.keys(sessionStorage).sort().reverse();
  const lastScrollPosKey = sessionStorageKeys.find((key) =>
    key.includes(`@@scroll|${window.location.pathname}`),
  );

  if (lastScrollPosKey) {
    const scrollY = sessionStorage.getItem(lastScrollPosKey);
    if (scrollY && !isNaN(scrollY)) {
      setTimeout(
        () => {
          window.scrollTo({
            top: parseFloat(scrollY),
            left: 0,
          });
        },
        isChromeBased ? 1000 : 0,
      );
    }
  }
};

// export const shouldUpdateScroll = ({ routerProps: { location }, getSavedScrollPosition }) => {
// 	window.history.scrollRestoration = 'manual';
// 	const [leftPos, topPos] = getSavedScrollPosition(location) || [0, 0];

// 	setTimeout(() => {
// 		window.scrollTo({
// 			top: topPos,
// 			left: leftPos,
// 			// behavior: 'smooth',
// 		});
// 	}, 0);

// 	return false;
// };
